import type { RxCollection } from 'rxdb';
import type { AppDatabase } from '../../db/database';
import { defineStoredJson } from '../../utils/storage';

/**
 * Documents the server sent back instead of accepting.
 *
 * A push answers with the states it did NOT take: a genuine conflict (someone
 * else changed the record first) and a refused write look exactly the same on
 * the wire, and both are handled without anybody being told.
 *
 * That silence is what makes a stuck queue impossible to explain, and asking
 * the server afterwards cannot help: once a change has been rolled back the
 * local document IS the server's state, so a comparison says "identical" for
 * the change that landed and the one thrown away alike. The only moment the
 * difference exists is when the answer comes in — so it is written down then.
 *
 * A diagnostic, not work: it may be lost to a wipe, and it is capped.
 */

export interface PushRejection {
    collection: string;
    docId: string;
    /** How often this document has come back, and when it last did. */
    count: number;
    firstAt: number;
    lastAt: number;
    /**
     * The change was not merged but DROPPED — the server refused it and sent
     * back the state it already had (see features/sync/conflictHandler). The
     * one that cost somebody their typing, and the only one worth telling them
     * about.
     */
    rolledBack?: boolean;
    /** Whether it has been put in front of the user yet. */
    seen?: boolean;
}

/** More than this and the oldest goes; a repeating rejection is repetitive. */
const MAX_ENTRIES = 100;

const rejections = defineStoredJson<PushRejection[]>({
    key: 'push_rejections',
    fallback: () => [],
    decode: (parsed) => (Array.isArray(parsed) ? parsed : undefined)
});

function key(collection: string, docId: string): string {
    return `${collection}/${docId}`;
}

/** Note that the server sent these documents back rather than taking them. */
export function recordRejections(collection: string, docIds: string[]): void {
    if (docIds.length === 0) {
        return;
    }
    const now = Date.now();
    const known = new Map(
        rejections
            .read()
            .map((entry) => [key(entry.collection, entry.docId), entry])
    );
    for (const docId of docIds) {
        const entry = known.get(key(collection, docId));
        if (entry) {
            entry.count += 1;
            entry.lastAt = now;
        } else {
            known.set(key(collection, docId), {
                collection,
                docId,
                count: 1,
                firstAt: now,
                lastAt: now
            });
        }
    }
    const all = [...known.values()].sort(
        (left, right) => right.lastAt - left.lastAt
    );
    rejections.write(all.slice(0, MAX_ENTRIES));
}

/**
 * Say that this document's change was dropped rather than merged. Called from
 * the conflict handler, the only place that can tell the difference — by then
 * the local document has been overwritten with the server's state, and nothing
 * afterwards can see what was lost.
 */
export function markRolledBack(collection: string, docId: string): void {
    const all = rejections.read();
    const entry = all.find(
        (candidate) =>
            candidate.collection === collection && candidate.docId === docId
    );
    const now = Date.now();
    if (entry) {
        entry.rolledBack = true;
        entry.seen = false;
        entry.lastAt = now;
        rejections.write(all);
        return;
    }
    rejections.write(
        [
            {
                collection,
                docId,
                count: 1,
                firstAt: now,
                lastAt: now,
                rolledBack: true,
                seen: false
            },
            ...all
        ].slice(0, MAX_ENTRIES)
    );
}

/**
 * The ids among `docIds` whose document has been DELETED here since — by the
 * office or anyone else. Only a tombstone counts: a document that is merely
 * absent may still be on its way (a fresh database pulling, see
 * offline-work-survives-broken-db), and an unknown collection proves nothing.
 */
export function deletedLocally(
    db: AppDatabase
): (collection: string, docIds: string[]) => Promise<Set<string>> {
    return async (collection, docIds) => {
        const target = (db.collections as Record<string, RxCollection>)[
            collection
        ];
        if (!target) {
            return new Set();
        }
        const primaryPath = target.schema.primaryPath as string;
        const rows = (await target.storageInstance.findDocumentsById(
            docIds,
            true
        )) as Record<string, unknown>[];
        return new Set(
            rows
                .filter((row) => row._deleted === true)
                .map((row) => String(row[primaryPath]))
        );
    };
}

/** Dropped changes the user has not been shown yet. */
export function unseenRollbacks(): PushRejection[] {
    return rejections.read().filter((entry) => entry.rolledBack && !entry.seen);
}

/** Every dropped change still on record, newest first. */
export function rolledBackChanges(): PushRejection[] {
    return rejections.read().filter((entry) => entry.rolledBack);
}

/** They have been shown — stop announcing them, keep the record. */
export function markRollbacksSeen(): void {
    const all = rejections.read();
    if (!all.some((entry) => entry.rolledBack && !entry.seen)) {
        return;
    }
    for (const entry of all) {
        if (entry.rolledBack) {
            entry.seen = true;
        }
    }
    rejections.write(all);
}

type DocRef = { collection: string; docId: string };

/**
 * Take these documents off the list of dropped changes. The entry itself
 * stays: a document still queued explains its queue row with it.
 */
function settleRollbacks(docs: readonly DocRef[]): void {
    if (docs.length === 0) {
        return;
    }
    const done = new Set(docs.map((doc) => key(doc.collection, doc.docId)));
    const all = rejections.read();
    let changed = false;
    for (const entry of all) {
        if (entry.rolledBack && done.has(key(entry.collection, entry.docId))) {
            delete entry.rolledBack;
            delete entry.seen;
            changed = true;
        }
    }
    if (changed) {
        rejections.write(all);
    }
}

/**
 * The user has dealt with the dropped changes they were SHOWN. One arriving
 * while the list was open is not among them and stays.
 */
export function dismissRollbacks(shown: readonly DocRef[]): void {
    settleRollbacks(shown);
}

/**
 * How long a fresh rollback is left alone. A refused DELETE is marked while
 * the document here is still the user's tombstone; the server's state is
 * written back a moment later.
 */
const SETTLE_MS = 60_000;

/**
 * Settle the dropped changes whose document has been deleted since: nothing
 * is left to enter again. The caller says what is deleted — this module has
 * no database. One lookup per collection.
 */
export async function pruneRollbacks(
    findDeleted: (collection: string, docIds: string[]) => Promise<Set<string>>
): Promise<void> {
    const settled = Date.now() - SETTLE_MS;
    const byCollection = new Map<string, string[]>();
    for (const entry of rolledBackChanges()) {
        if (entry.lastAt >= settled) {
            continue;
        }
        const ids = byCollection.get(entry.collection) ?? [];
        ids.push(entry.docId);
        byCollection.set(entry.collection, ids);
    }
    const gone: DocRef[] = [];
    for (const [collection, docIds] of byCollection) {
        for (const docId of await findDeleted(collection, docIds)) {
            gone.push({ collection, docId });
        }
    }
    settleRollbacks(gone);
}

/** Fires on a change here and in another window. */
export const subscribeRejections = rejections.subscribe;

/** The rejection recorded for one document, if there is one. */
export function rejectionFor(
    collection: string,
    docId: string
): PushRejection | undefined {
    return rejections
        .read()
        .find(
            (entry) => entry.collection === collection && entry.docId === docId
        );
}

/** Forget them — used when the queue entries they explain are dropped. */
export function forgetRejections(
    docs: readonly { collection: string; docId: string }[]
): void {
    if (docs.length === 0) {
        return;
    }
    const gone = new Set(docs.map((doc) => key(doc.collection, doc.docId)));
    const kept = rejections.read().filter(
        (entry) =>
            // A DROPPED change outlives the queue entry that led us to it: the
            // queue entry is bookkeeping, the fact that somebody's typing was
            // thrown out is what they still have to be told about.
            entry.rolledBack || !gone.has(key(entry.collection, entry.docId))
    );
    if (kept.length === 0) {
        rejections.clear();
        return;
    }
    rejections.write(kept);
}
